import AgoraRTC, {
  IAgoraRTCClient,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
} from "agora-rtc-sdk-ng";
import axios from "axios";
import {fetchAppCheckToken} from "../firebase/FirebaseService";

AgoraRTC.setLogLevel(3);

interface AgoraRTCState {
  localAudioTrack: IMicrophoneAudioTrack | null;
  client: IAgoraRTCClient | null;
  state: "idle" | "calling";
  remoteAudioTrack: IRemoteAudioTrack | null;
}

const rtc: AgoraRTCState = {
  localAudioTrack: null,
  client: null,
  state: "idle",
  remoteAudioTrack: null,
};

export async function getAgoraToken(number: string) {
  try {
    const url = `${process.env.REACT_APP_CORLMI_API_URL}/api/v2/token`;
    const appCheckToken = await fetchAppCheckToken();

    const headers = {
      "Content-Type": "application/json",
      "x-firebase-appcheck": appCheckToken,
    };

    const data = {number, role: "caller"};
    console.debug(
      `Sending Generate Agora token payload: ${JSON.stringify(data)} to ${url}`
    );

    const response = await axios.post(url, data, {headers: headers});
    console.debug(
      `Received Generate Agora token response: ${JSON.stringify(response?.data)}`
    );

    return response.data;
  } catch (error: any) {
    console.error("Error generating token:", error);
    throw new Error(
      error?.response?.data?.error ||
      "Unable to initiate call"
    );
  }
}

export async function leaveChannel() {
  try {
    // Stop and close local track
    console.debug("Leaving channel and stopping local track");

    if (rtc.localAudioTrack) {
      rtc.localAudioTrack.stop();
      rtc.localAudioTrack.close();
      rtc.localAudioTrack = null;
    }

    // Stop remote track
    if (rtc.remoteAudioTrack) {
      rtc.remoteAudioTrack.stop();
      rtc.remoteAudioTrack = null;
    }

    // Leave Agora channel
    if (rtc.client) {
      await rtc.client.leave();
    }
  } catch (error) {
    console.error("Error leaving channel:", error);
  } finally {
    // rtc.localAudioTrack = null;
    // rtc.remoteAudioTrack = null;
    rtc.state = "idle";
    _connectedCallback = null;
    console.debug("Left channel and reset RTC state.");
  }
}

let _connectedCallback: (() => void) | null = null;

export async function initializeClient(
  endCallback: (error?: string) => void,
  connectedCallback: () => void
) {
  _connectedCallback = connectedCallback;
  // Ensure previous instance listeners are cleared if re-initialized
  if (rtc.client) {
    rtc.client.removeAllListeners();
  }

  rtc.client = AgoraRTC.createClient({mode: "rtc", codec: "vp8"});
  setupEventListeners(endCallback, connectedCallback);
}

function setupEventListeners(
  endCallback: (error?: string) => void,
  connectedCallback: () => void
) {
  if (!rtc.client) return;

  rtc.client.on("user-published", async (user, mediaType) => {
    try {
      console.log(`[agoraClient] user-published event received: user=${user.uid}, mediaType=${mediaType}`);
      if (mediaType === "audio") {
        if (!user.audioTrack) {
          await rtc.client?.subscribe(user, mediaType);
        }
        rtc.remoteAudioTrack = user.audioTrack || null;
        rtc.remoteAudioTrack?.play();
        connectedCallback();
      }
    } catch (e: any) {
      console.error("Error subscribing to remote user:", e, user);
      await leaveChannel();
      endCallback(e?.message || "Subscription error");
    }
  });

  rtc.client.on("user-unpublished", async (user) => {
    try {
      if (user.audioTrack) {
        await rtc.client?.unsubscribe(user);
      }
    } catch (error) {
      console.error("Error unsubscribing from remote user:", error, user);
    }
    await leaveChannel();
    endCallback();
  });

  rtc.client.on("user-left", async (user, reason) => {
    console.log("<<<<<<Callback>>>>>User left", user, reason);
    await leaveChannel();
    endCallback();
  });
  rtc.client.on("user-joined", async (user) => {
    console.log("[agoraClient] Remote user joined channel:", JSON.stringify(user));
  });
}

export async function joinChannel(
  appId: string,
  channel: string,
  token: string,
  uid: string | number
) {
  if (!rtc.client) {
    throw new Error("RTC Client not initialized. Call initializeClient first.");
  }

  // Agora requires all participants in the same channel to use the same UID type.
  // Mobile joins with integer UID (int.parse), so Web must join as numeric UID to avoid isolated namespaces.
  const numericUid = typeof uid === "string" ? parseInt(uid, 10) : uid;
  const validUid = !isNaN(numericUid) && numericUid !== 0 ? numericUid : uid;

  console.debug(`Joining channel ${channel}, uid: ${validUid} (type: ${typeof validUid})`);
  const result = await rtc.client.join(appId, channel, token, validUid);
  console.debug("Join result: ", result);
  await publishLocalAudio();
  rtc.state = "calling";
  console.debug("Successfully joined and published local audio.");

  // Check if any remote users already in the channel published audio before local join finished
  if (rtc.client.remoteUsers.length > 0) {
    console.debug(`[agoraClient] Found ${rtc.client.remoteUsers.length} existing remote user(s) on join`);
    for (const remoteUser of rtc.client.remoteUsers) {
      console.debug(`[agoraClient] Existing remote user: ${remoteUser.uid}, hasAudio: ${remoteUser.hasAudio}`);
      if (remoteUser.hasAudio) {
        try {
          if (!remoteUser.audioTrack) {
            console.debug(`[agoraClient] Subscribing to existing remote user audio: ${remoteUser.uid}`);
            await rtc.client.subscribe(remoteUser, "audio");
          }
          rtc.remoteAudioTrack = remoteUser.audioTrack || null;
          rtc.remoteAudioTrack?.play();
          if (_connectedCallback) {
            _connectedCallback();
          }
        } catch (subErr) {
          console.error("[agoraClient] Error subscribing to existing remote user:", subErr);
        }
      }
    }
  }
}

async function publishLocalAudio() {
  try {
    // Agora SDK handles microphone acquisition internally
    // await navigator.mediaDevices.getUserMedia({audio: true});

    rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    
    if (rtc.client) {
      await rtc.client.publish([rtc.localAudioTrack]);
    }
  } catch (error) {
    console.error("Microphone permission denied or failed to create track:", error);
    throw new Error("Microphone access required for calls");
  }
}

export async function setLocalAudioMuted(muted: boolean): Promise<void> {
  console.log(`[agoraClient] setLocalAudioMuted called with muted=${muted}`);
  try {
    if (rtc?.localAudioTrack && rtc?.client) {
      if (muted) {
        // Set track state to muted first
        await rtc.localAudioTrack.setMuted(true);
        // Unpublish the track from the channel so no WebRTC packets are sent (essential for WebViews/iOS)
        await rtc.client.unpublish([rtc.localAudioTrack]);
        console.log(`[agoraClient] Local microphone muted and unpublished.`);
      } else {
        // Set track state to unmuted
        await rtc.localAudioTrack.setMuted(false);
        // Re-publish the track to the channel
        await rtc.client.publish([rtc.localAudioTrack]);
        console.log(`[agoraClient] Local microphone unmuted and re-published.`);
      }
    } else {
      console.warn("[agoraClient] No local audio track or client available to mute/unmute. rtc:", rtc);
    }
  } catch (error) {
    console.error("[agoraClient] Error toggling local audio track:", error);
    throw error;
  }
}

export async function toggleSpeakerphone(useSpeaker: boolean): Promise<void> {
  console.log(`[agoraClient] toggleSpeakerphone called with useSpeaker=${useSpeaker}`);
  if (!rtc?.remoteAudioTrack) {
    console.warn("[agoraClient] No remote audio track available for speaker toggle. rtc:", rtc);
    return;
  }

  // 1. Try physical output routing using WebRTC setPlaybackDevice API (if supported)
  try {
    if (typeof rtc.remoteAudioTrack.setPlaybackDevice === "function") {
      const devices = await AgoraRTC.getPlaybackDevices();
      console.log("[agoraClient] Found playback devices:", devices);

      if (devices.length > 0) {
        let targetDevice: MediaDeviceInfo | undefined;

        if (useSpeaker) {
          targetDevice = devices.find((d) => {
            const label = d.label.toLowerCase();
            return label.includes("speaker") || label.includes("loudspeaker") || label.includes("speakerphone");
          });
        } else {
          targetDevice = devices.find((d) => {
            const label = d.label.toLowerCase();
            return label.includes("earpiece") || label.includes("receiver") || label.includes("handset");
          });
        }

        if (targetDevice) {
          await rtc.remoteAudioTrack.setPlaybackDevice(targetDevice.deviceId);
          console.log(`[agoraClient] Set remote audio output to: ${targetDevice.label}`);
          return;
        }

        if (!useSpeaker) {
          const defaultDevice = devices.find((d) => d.deviceId === "default" || d.label.toLowerCase().includes("default"));
          if (defaultDevice) {
            await rtc.remoteAudioTrack.setPlaybackDevice(defaultDevice.deviceId);
            console.log("[agoraClient] Reverted remote audio output to default device");
            return;
          }
        }
      }
    }
  } catch (deviceError) {
    console.warn("[agoraClient] Could not change playback device:", deviceError);
  }

  // 2. Playback control (Essential for iOS Safari/WKWebView where media element volume is read-only)
  try {
    if (useSpeaker) {
      await rtc.remoteAudioTrack.setVolume(100);
      rtc.remoteAudioTrack.play();
      console.log("[agoraClient] Speaker unmuted (volume 100, playing remote track)");
    } else {
      await rtc.remoteAudioTrack.setVolume(0);
      rtc.remoteAudioTrack.stop();
      console.log("[agoraClient] Speaker muted (volume 0, stopped remote track)");
    }
  } catch (volumeError) {
    console.error("[agoraClient] Error setting remote audio track volume/playback:", volumeError);
  }
}
